import React, { useEffect, useRef, useState } from "react";
export { default as NotificationsMenu } from './NotificationsMenu';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { localizeNotificationParams } from "./notificationParams";
import { ArrowRight, Check, X } from 'lucide-react';
import Pagination from '../Pagination';
import { createUrl } from "../../utils";
import Icon from "../AppIcon";
import { notificationIconMap as iconMap } from './notificationIconMap';
import Button from "../ui/Button";
import { cn } from "../../utils/cn";
import dateFormat from "dateformat";
import { GetNotificationsFirstPagePerUser, GetNotificationsPerUser, markNotificationAsRead } from "../../api/General/Notification";
import {
  DECREMENT_NOTIFICATIONS_UNREAD_COUNT,
  SET_NOTIFICATION_UNREAD_COUNT,
} from "../../store/redux/actions/notificationActions";
import type { PendingActionItem } from "../../api/pendingActions";
import DecisionDialog from "../pendingActions/DecisionDialog";
import { getSourceRoute, completeActionLabel, isDispatchable, offersNegativeDecision } from "../pendingActions/dispatch";
import { usePendingActionDecision, pendingActionKey } from "../pendingActions/usePendingActionDecision";
import { ToastRenderer } from "../../hooks/useToast";
import { useActionableIndex } from './useActionableIndex';
import { useComplianceCountry } from '../../hooks/useComplianceCountry';
import { notificationActionKey, resolvedOutcomeLabel, type ActionableNotification, type ResolvedOutcome } from './types';

interface INotificationListProps {
  type?: number | undefined;
  full?: boolean;
  showHeader?: boolean;
  markAllReadSignal?: number;
  onUnreadCountChange?: (count: number) => void;
  refreshSignal?: number;
  /**
   * Raised while a modal owned by this list is open. The bell popover uses it to
   * stay open: the confirm dialog renders in its own portal, so without this the
   * first click inside it reads as "outside the popover" and takes the dialog
   * down with the popover.
   */
  onInteractionLockChange?: (locked: boolean) => void;
}

const NotificationList = (props: INotificationListProps) => {
  const { type, full, showHeader = true, markAllReadSignal, refreshSignal, onUnreadCountChange, onInteractionLockChange } = props;
  const { t, i18n } = useTranslation(['common', 'requests']);
  const user = useSelector((state: any) => state.user);
  const userId = useSelector((state: any) => state.user.userId);
  const role = useSelector((state: { user?: { role?: string } }) => state.user?.role) || '';
  const isManager = useSelector((state: { user?: { isManager?: boolean } }) => state.user?.isManager) || false;
  // Non-role route gates (see getSourceRoute). Effective shift-swap enablement is the
  // per-user override else the company default, exactly as ShiftSwapEnabledRoute resolves it —
  // resolving it differently here would offer a deep link the guard bounces to '/'.
  const shiftSwapConfig = useSelector((state: { user?: { userRules?: { shiftSwapConfig?: string } } }) => state.user?.userRules?.shiftSwapConfig);
  const companyShiftSwapEnabled = useSelector((state: { user?: { company?: { shiftSwapEnabled?: boolean } } }) => state.user?.company?.shiftSwapEnabled);
  const shiftSwapEnabled =
    shiftSwapConfig === 'Enabled' ? true :
    shiftSwapConfig === 'Disabled' ? false :
    !!companyShiftSwapEnabled;
  const complianceCountry = useComplianceCountry();
  const routeContext = { role, isManager, shiftSwapEnabled, complianceCountry };
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [notifications, setNotifications] = useState<ActionableNotification[]>([]);
  const [notificationsTotalCount, setNotificationsTotalCount] = useState(0);
  const [notificationsPageCount] = useState(6);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  /** Rows decided from here this session: the outcome replaces the buttons at once. */
  const [resolved, setResolved] = useState<Record<number, ResolvedOutcome>>({});

  const { index: actionIndex, truncated: actionsTruncated, refresh: refreshActionIndex } = useActionableIndex(notifications, true);
  // Maps the in-flight pending-action back to the notification row that started it.
  const decidingRowRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    setSelectedPageIndex(0);
    loadNotifications(type, 0, notificationsPageCount);
  }, [type, userId]);

  // When parent triggers mark-all-read, update local list instantly for UX
  useEffect(() => {
    if (markAllReadSignal) {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    }
  }, [markAllReadSignal]);

  // External refresh trigger (e.g., page-level reload button)
  useEffect(() => {
    if (typeof refreshSignal !== 'undefined') {
      setSelectedPageIndex(0);
      loadNotifications(type, 0, notificationsPageCount);
    }
  }, [refreshSignal]);

  const loadNotifications = async (notificationType: number | undefined, pageNumber: number, pageSize: number) => {
    if (!userId) {
      console.error("User ID is not available");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { payload } = full ?
        await GetNotificationsPerUser(userId, notificationType ?? 0, pageNumber + 1, pageSize)
        : await GetNotificationsFirstPagePerUser(userId);
      setNotifications(payload.notificationsList);
      if (onUnreadCountChange) {
        const unread = payload.notificationsList.filter((n: any) => !n.isRead).length;
        onUnreadCountChange(unread);
      }
      setNotificationsTotalCount(payload.notificationsTotalCount);
    } catch (e: any) {
      console.error(e);
      setError(e?.message || t('Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: number) => {
    // Only an actually-unread row moves the bell badge.
    const wasUnread = notifications.some(n => n.id === id && !n.isRead);
    try {
      await markNotificationAsRead(id);
      setNotifications(prev => {
        const next = prev.map(n => n.id === id ? { ...n, isRead: true } : n);
        if (onUnreadCountChange) onUnreadCountChange(next.filter(n => !n.isRead).length);
        return next;
      });
      if (wasUnread) dispatch({ type: DECREMENT_NOTIFICATIONS_UNREAD_COUNT });
    } catch (e) {
      console.error(e);
    }
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter(n => !n.isRead);
    if (!unread.length) return;
    // Fire & wait sequentially to avoid hammering backend
    for (const n of unread) {
      try { await markNotificationAsRead(n.id); } catch (e) { console.error(e); }
    }
    setNotifications(prev => {
      const next = prev.map(n => ({ ...n, isRead: true }));
      if (onUnreadCountChange) onUnreadCountChange(0);
      return next;
    });
    dispatch({ type: SET_NOTIFICATION_UNREAD_COUNT, payload: 0 });
  };

  // --- Quick actions ---------------------------------------------------------
  // The decision runs through the exact sequence the Pending Actions inbox uses
  // (confirm → dispatch → toast → refetch → broadcast); this surface adds only
  // "mark the notification read and show the outcome in place".

  const decision = usePendingActionDecision({
    onChanged: refreshActionIndex,
    onResolved: (item, positive) => {
      const key = pendingActionKey(item);
      const notificationId = decidingRowRef.current.get(key);
      decidingRowRef.current.delete(key);
      if (notificationId === undefined) return;
      setResolved(prev => ({ ...prev, [notificationId]: { positive, actionStyle: item.actionStyle } }));
      markAsRead(notificationId);
    },
  });

  useEffect(() => {
    onInteractionLockChange?.(decision.decisionTarget !== null);
  }, [decision.decisionTarget, onInteractionLockChange]);

  const startDecision = (notificationId: number, item: PendingActionItem, positive: boolean) => {
    decidingRowRef.current.set(pendingActionKey(item), notificationId);
    decision.requestDecision(item, positive);
  };

  /** Dismissing the confirm dialog abandons the decision, so drop its row mapping. */
  const cancelDecision = () => {
    const target = decision.decisionTarget;
    if (target) decidingRowRef.current.delete(pendingActionKey(target.item));
    decision.closeDecision();
  };

  const openNotification = async (item: ActionableNotification) => {
    await markAsRead(item.id);
    const url = createUrl(item);
    if (url) navigate(url);
  };

  /**
   * Where an OpenOnly quick action would land: the page that owns the request,
   * falling back to the notification's own link. Undefined when neither exists —
   * a pageless source (Offboarding, RestrictedHoliday), a route this role would
   * be bounced from, and no notificationUrl on the row. The bell has no Details
   * fallback, so the button is withheld rather than shipped as a no-op.
   */
  const openTargetFor = (item: ActionableNotification, pending: PendingActionItem): string | undefined =>
    getSourceRoute(pending, routeContext) ?? createUrl(item);

  /** OpenOnly rows go to the page that owns the request, falling back to the notification's own link. */
  const openSourcePage = async (item: ActionableNotification, pending: PendingActionItem) => {
    await markAsRead(item.id);
    const url = openTargetFor(item, pending);
    if (url) navigate(url);
  };

  /**
   * Title/body params arrive as a server-built JSON string. A single malformed
   * row used to throw inside render and blank the whole list (bell and page):
   * the notification surface is the last place that should be able to take a
   * screen down, and this wave adds 41 new producers of these blobs.
   */
  const safeParams = (raw: string | undefined | null): Record<string, unknown> => {
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  };

  const relativeTime = (date: Date) => {
    const diffMs = Date.now() - date.getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d`;
    const wk = Math.floor(day / 7);
    if (wk < 4) return `${wk}w`;
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo}mo`;
    const yr = Math.floor(day / 365);
    return `${yr}y`;
  };

  /** The one-time verification code some asset notifications carry. */
  function otpFor(item: ActionableNotification): string | undefined {
    if (item.notificationType?.name !== 'AssetOtpVerification') return undefined;
    const otp: unknown = safeParams(item.bodyParams).otp;
    if (typeof otp !== 'string' || otp.length === 0) return undefined;
    if (typeof navigator === 'undefined' || !navigator.clipboard) return undefined;
    return otp;
  }

  /**
   * The row's quick action(s), or null when there is nothing this surface can
   * honestly offer. Every branch here must end in something that works: the bell
   * is a popover with no Details fallback, so a button that silently does
   * nothing is worse than no button at all.
   */
  function renderQuickActions(item: ActionableNotification, pending: PendingActionItem, title: string): React.ReactNode | null {
    const busy = decision.busyFor(pending);
    const disabled = busy !== null;
    // A decision button is only honest when `dispatchDecision` can carry it. The
    // server pairs each source with an actionStyle, but that pairing is across a
    // wire boundary: fall back to Open rather than paint an Approve that can only
    // return NotDispatchable.
    const style = isDispatchable(pending.source) ? pending.actionStyle : 'OpenOnly';
    switch (style) {
      case 'Decision2':
      case 'Accept2': {
        const negative = style === 'Decision2' ? t('Reject') : t('Decline');
        const positive = style === 'Decision2' ? t('Approve') : t('Accept');
        return (
          <>
            {/* CO-780 — the third surface that paints this pair, and it posts the same endpoints, so
                it takes the same gate: a referee row whose match has left `Scheduled` can be accepted
                but not declined, and the bell has no Details fallback to explain a button that only
                400s. `offersNegativeDecision` fails open (see its comment), so this withholds nothing
                it was not told about. */}
            {offersNegativeDecision(pending) && (
            <Button
              variant="outline"
              size="xs"
              disabled={disabled}
              loading={busy === 'negative'}
              onClick={() => startDecision(item.id, pending, false)}
              aria-label={`${negative}: ${title}`}
              data-testid="notification-quick-reject"
            >
              {negative}
            </Button>
            )}
            <Button
              variant="success"
              size="xs"
              disabled={disabled}
              loading={busy === 'positive'}
              onClick={() => startDecision(item.id, pending, true)}
              aria-label={`${positive}: ${title}`}
              data-testid="notification-quick-approve"
            >
              {positive}
            </Button>
          </>
        );
      }
      case 'Complete1': {
        const complete = t(completeActionLabel(pending.source));
        return (
          <Button
            variant="success"
            size="xs"
            disabled={disabled}
            loading={busy === 'positive'}
            onClick={() => startDecision(item.id, pending, true)}
            aria-label={`${complete}: ${title}`}
            data-testid="notification-quick-complete"
          >
            {complete}
          </Button>
        );
      }
      case 'OpenOnly':
      default:
        if (!openTargetFor(item, pending)) return null;
        return (
          <Button
            variant="outline"
            size="xs"
            onClick={() => openSourcePage(item, pending)}
            aria-label={`${t('Open')}: ${title}`}
            data-testid="notification-quick-open"
          >
            {t('Open')}
          </Button>
        );
    }
  }

  function createListItem(item: ActionableNotification) {
    const iconName = (item.notificationType?.name ? iconMap[item.notificationType.name] : undefined) || 'Info';
    const createdLocal = new Date(new Date(item.createdOn).getTime() - (new Date().getTimezoneOffset() * 60 * 1000));
    const timeFormatted = dateFormat(createdLocal, full ? 'dd/mm/yyyy h:MM:ss TT' : 'dd mmm h:MM TT');

    const actionKey = notificationActionKey(item);
    const outcome = resolved[item.id];
    // Buttons exist only while the queue still holds the item: resolved anywhere,
    // or viewed by someone who cannot action it, and the row is informational.
    const pending = !outcome && actionKey ? actionIndex.get(actionKey) : undefined;
    const otp = otpFor(item);
    const title = t(item.title ?? '', { ...localizeNotificationParams(item.titleParams, t, i18n), nsSeparator: false });
    // Resolved before the footer so a row whose actions all withheld themselves
    // renders no empty, padded strip.
    const quickActions = pending ? renderQuickActions(item, pending, title) : null;
    const hasFooter = !!quickActions || !!outcome || !!otp;

    // Unread tint.
    //
    // 🔴 NOT `bg-accent`. Tailwind's `accent` resolves to `--color-accent`, which tokens.css
    // defines as #f59e0b — the SAME value as `--color-warning`. So the shadcn "accent" role (a
    // subtle hover/selected surface) is literally the warning colour in this codebase, and every
    // unread row of EVERY type rendered as a saturated amber alert with gray-500 body text on top
    // (~1.9:1, a WCAG failure). Routine subscription activity looked like an incident. This is a
    // SYSTEM-WIDE token defect, not a per-type severity mapping — there is no severity mapping at
    // all — so it is corrected here at the point of use rather than by repainting `--color-accent`
    // for the whole app.
    //
    // Restrained per DESIGN.md: primary at 5% over the card. Text keeps its normal contrast
    // against the card, and the tint follows the tenant's own primary rather than a hardcoded hue.
    // Unread stays carried by the dot + the sr-only "Unread" label too, so the tint is
    // reinforcement, never the sole signal.
    //
    // Hover is 10% primary: one deliberate step darker than the unread tint, so hovering an
    // unread row still moves. It replaces `hover:bg-accent`, which flashed the warning amber
    // under the pointer on every row, read or unread.
    return (
      <div
        key={item.id}
        className={cn('transition-colors duration-200', !item.isRead && 'bg-primary/5')}
      >
        {/* Navigation affordance. A div, not a button: the action row below has to
            be a sibling, and interactive elements cannot nest inside a <button>.
            data-action-*: which record the row is about, for E2E reads (CO-375 e13-default-off). The
            rendered text of a leave-request row never names the leave, so two requests look identical.
            data-notification-id / data-read: the row id a push carries as "notificationId" (CO-961), and
            its read state, for E2E reads. Never rendered as text. */}
        <div
          role="button"
          tabIndex={0}
          data-testid="notification-row"
          data-notification-id={item.id}
          data-read={item.isRead ? 'true' : 'false'}
          data-action-source={item.actionSource || undefined}
          data-action-entity-id={item.actionEntityId || undefined}
          className="w-full flex items-start gap-3 py-3 px-4 text-start cursor-pointer hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          onClick={() => openNotification(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openNotification(item);
            }
          }}
        >
          <div className="relative flex items-start">
            {/* The type icon repeats the title in picture form: decorative to AT. */}
            <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" aria-hidden="true">
              <Icon name={iconName} size={24} style={{ color: user?.companyPrimaryColor }} />
            </div>
            {!item.isRead && (
              <>
                {/* Read state is carried by a coloured dot and a background tint,
                    both invisible to a screen reader. The label rides inside the
                    row's own accessible name, so it is announced ahead of the
                    title instead of as a separate stop. */}
                <span className="sr-only">{t('Unread')}</span>
                <div
                  className="absolute top-0 end-0 w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: user?.companyPrimaryColor || 'var(--primary)' }}
                  aria-hidden="true"
                />
              </>
            )}
          </div>
          <div className="flex-1 min-w-0 me-1">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-sm font-semibold text-foreground leading-tight">{title}</span>
              <span className="text-xs text-muted-foreground" title={timeFormatted}>{relativeTime(createdLocal)}</span>
            </div>
            {/* `text-ellipsis` never did anything here: it needs `whitespace-nowrap` or a
                line-clamp to have an effect, and this body is deliberately allowed to wrap.
                `break-words` is what actually matters — subscription bodies carry long unbroken
                tokens (company slugs, rule codes, ISO timestamps) that pushed the row past the
                viewport at 320px. `max-w-[75ch]` holds the measure inside the readable band on a
                wide screen instead of running the full page width. */}
            <span className="text-sm text-muted-foreground mt-1 block max-w-[75ch] break-words">
              {t(item.body ?? '', { ...localizeNotificationParams(item.bodyParams, t, i18n), nsSeparator: false })}
            </span>
            {pending && (
              <span className="text-xs font-medium text-primary mt-1 block">{t('Action required')}</span>
            )}
          </div>
        </div>

        {/* Sibling action row: aligned to the title, never inside the tap target. */}
        {hasFooter && (
          <div className="flex flex-wrap items-center gap-2 ps-16 pe-4 pb-3">
            {quickActions}
            {outcome && (
              <span
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
                data-testid="notification-resolved"
              >
                {outcome.positive
                  ? <Check size={14} className="text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                  : <X size={14} className="text-muted-foreground" aria-hidden="true" />}
                {t(resolvedOutcomeLabel(outcome))}
              </span>
            )}
            {otp && (
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  navigator.clipboard.writeText(otp).then(() => {
                    setCopiedId(item.id);
                    setTimeout(() => setCopiedId(null), 2000);
                  }).catch(() => { /* clipboard write failed silently */ });
                }}
                data-testid="notification-copy-code"
              >
                {copiedId === item.id ? t('notifications.copied') : t('notifications.copyCode')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  const onPageChange = (page: number): void => {
    const zeroBased = page - 1;
    setSelectedPageIndex(zeroBased);
    loadNotifications(type, zeroBased, notificationsPageCount);
  };

  const skeletons = Array.from({ length: 4 }).map((_, i) => (
    <div key={`skeleton-${i}`} className="flex items-start gap-3 py-3 px-4 animate-pulse">
      <div className="w-9 h-9 rounded-lg bg-muted flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-muted rounded w-3/5" />
        <div className="h-3 bg-muted rounded w-4/5" />
      </div>
    </div>
  ));

  const showEmpty = !loading && !notifications.length;

  return (
    <div>
      {full && showHeader && (
        <div className="mb-3">
          <div className="flex items-center justify-between flex-wrap gap-y-1">
            <h3 className="text-lg font-semibold text-foreground">{t('Notifications')}</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{notificationsTotalCount} {t('total')}</span>
              <div className="w-px h-4 bg-border" />
              <Button variant="ghost" size="sm" disabled={!notifications.some(n => !n.isRead)} onClick={markAllAsRead}>
                {t('Mark all as read')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="w-full">
        {loading ? skeletons : notifications.map((x) => createListItem(x))}
      </div>
      {/* Hydration reads only the first page of the queue, so an approver with a
          deeper queue loses inline buttons on rows past it — indistinguishable
          from "already resolved". Point them at the full queue instead. */}
      {actionsTruncated && (
        <div className="border-t border-border px-4 pt-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pending-actions')} data-testid="notif-actions-truncated">
            {t('Some quick actions are not shown here')}
            <ArrowRight size={14} className="ms-1.5 rtl:-scale-x-100" />
          </Button>
        </div>
      )}
      {showEmpty && (
        <div className="text-center py-10 px-4">
          <Icon name="Bell" size={40} className="text-muted-foreground/40 mx-auto mb-2" />
          {/* Both keys are translated in en/ar/fr. The previous literals
              ("You are all caught up!" / "No notifications to show") existed in
              no bundle, so Arabic and French readers got raw English here. */}
          <p className="text-base font-medium text-foreground">{t("You're all caught up")}</p>
          <p className="text-sm text-muted-foreground">{t('No notifications yet')}</p>
        </div>
      )}
      {error && (
        <div className="mt-2 p-3 rounded-lg bg-destructive/10 text-destructive">
          <p className="text-sm">{error}</p>
          <Button variant="ghost" size="sm" onClick={() => loadNotifications(type, selectedPageIndex, notificationsPageCount)} className="mt-1">
            {t('Retry')}
          </Button>
        </div>
      )}
      {full && !loading && !error && notificationsTotalCount > notificationsPageCount && (
        <Pagination
          currentPage={selectedPageIndex + 1}
          totalPages={Math.max(1, Math.ceil(notificationsTotalCount / notificationsPageCount))}
          onPageChange={onPageChange}
        />
      )}

      <DecisionDialog
        target={decision.decisionTarget}
        submitting={decision.submitting}
        onClose={cancelDecision}
        onConfirm={decision.confirmDecision}
      />
      <ToastRenderer toast={decision.toast} onDismiss={decision.dismissToast} />
    </div>
  );
};

export default NotificationList;
